import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import { RootReducer } from 'redux/reducers';
import { selectFrame, selectActiveChat } from 'redux/db/reducers';
import { setCenteredFrameId, setPendingChatInput } from 'redux/mode/actions';
import { useImageLoading } from 'hooks/useImageLoading';
import { frameImageUrl, frameSiteUrl } from 'utils';
import { FrameStatus, ChatEntryRestriction, UserChatEntry } from 'dragonmouse-shared/interfaces';
import { Loader } from 'components/Widget/Loader';
import Icon, { IconType } from 'components/Icon';
import { Direction, splitMessageWithFrames } from 'redux/db/util';
import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import type { MouseEvent, TouchEvent } from 'react';
import * as S from './styles';

interface Props {
	frameId: string;
	// when sceneControls is true, the FrameGallery UI supports scene actions
	//  - up/down/left/right arrows
	//  - swipe
	//  - add to chat
	sceneControls?: boolean;

	// if set
	//  - shows a close button which calls this function
	//  - after addToChat is called, calls this function
	onClose?: () => void;
}

// Grid of frames that we move through with the arrow keys (not a full grid -- more like a list of rows of different lengths)
interface Grid {
	rows: string[][]; // rows are of different lengths
	getAdjacentFrame: (frameId: string, direction: Direction) => string | null;
}

const FrameGallery = ({ frameId, sceneControls, onClose }: Props) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const [controlsVisible, setControlsVisible] = useState(true);
	const hideControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);

	const { frame, activeChat, pendingChatInputs } = useSelector(
		(r: RootReducer) => ({
			frame: selectFrame(r.db, frameId),
			activeChat: selectActiveChat(r.db, r.mode.activeChatId),
			pendingChatInputs: r.mode.pendingChatInputs,
		}),
		shallowEqual
	);

	const imageSrc = frame && frameImageUrl(frame);
	const siteUrl = frame && frameSiteUrl(frame);
	const [isImageLoading, handleImageRef] = useImageLoading(imageSrc ?? '');

	// Build chat frame navigation structure
	const grid: Grid = useMemo(() => {
		if (!activeChat || !activeChat.entries) {
			return { rows: [], getAdjacentFrame: () => null };
		}

		const rows: string[][] = [];

		// Parse chat entries to build the 2D grid structure
		activeChat.entries.forEach((entry) => {
			if (
				entry.kind === 'system' &&
				(!entry.restriction || entry.restriction === ChatEntryRestriction.USER)
			) {
				// Parse system message for frames
				const parts = splitMessageWithFrames(entry.message);
				parts.forEach((part) => {
					if (part.type === 'frames') {
						const frameIds = part.frameIds.filter((id) => id.trim() !== '');
						if (frameIds.length > 0) {
							rows.push(frameIds);
						}
					}
				});
			} else if (entry.kind === 'user') {
				// Parse user message for source frames
				const userEntry = entry as UserChatEntry;
				if (userEntry.sourceFrameIds && userEntry.sourceFrameIds.length > 0) {
					rows.push(userEntry.sourceFrameIds);
				}
			}
		});

		// Create navigation function
		const getAdjacentFrame = (frameId: string, direction: Direction): string | null => {
			// Find current position in grid
			let currentRow = -1;
			let currentCol = -1;
			for (let row = 0; row < rows.length; row++) {
				const col = rows[row].indexOf(frameId);
				if (col !== -1) {
					currentRow = row;
					currentCol = col;
					break;
				}
			}

			if (currentRow === -1 || currentCol === -1) return null;

			switch (direction) {
				case Direction.LEFT:
					// Move left within the same row
					return currentCol > 0 ? rows[currentRow][currentCol - 1] : null;

				case Direction.RIGHT:
					// Move right within the same row
					return currentCol < rows[currentRow].length - 1 ? rows[currentRow][currentCol + 1] : null;

				case Direction.UP:
					// Move up to previous row, same column position
					if (currentRow > 0) {
						const prevRow = rows[currentRow - 1];
						// Try same column, then closest available position
						if (currentCol < prevRow.length) {
							return prevRow[currentCol];
						} else if (prevRow.length > 0) {
							// Use last position in previous row
							return prevRow[prevRow.length - 1];
						}
					}
					return null;

				case Direction.DOWN:
					// Move down to next row, same column position
					if (currentRow < rows.length - 1) {
						const nextRow = rows[currentRow + 1];
						// Try same column, then closest available position
						if (currentCol < nextRow.length) {
							return nextRow[currentCol];
						} else if (nextRow.length > 0) {
							// Use last position in next row
							return nextRow[nextRow.length - 1];
						}
					}
					return null;

				default:
					return null;
			}
		};

		return { rows, getAdjacentFrame };
	}, [activeChat]);

	// iOS photo viewer functionality
	const toggleControls = useCallback(() => {
		setControlsVisible((prev) => !prev);
	}, []);

	const hideControls = useCallback(() => {
		setControlsVisible(false);
	}, []);

	const showControls = useCallback(() => {
		setControlsVisible(true);
		// Auto-hide after 3 seconds
		if (hideControlsTimeoutRef.current) {
			clearTimeout(hideControlsTimeoutRef.current);
		}
		hideControlsTimeoutRef.current = setTimeout(() => {
			setControlsVisible(false);
		}, 3000);
	}, []);

	// Chat-based navigation handlers
	const navigateToFrame = useCallback(
		(targetFrameId: string | null) => {
			if (targetFrameId) {
				dispatch(setCenteredFrameId(targetFrameId));
			}
		},
		[dispatch]
	);

	const onArrowUp = useCallback(() => {
		const targetFrameId = grid.getAdjacentFrame(frameId, Direction.UP);
		navigateToFrame(targetFrameId);
	}, [frameId, grid, navigateToFrame]);

	const onArrowDown = useCallback(() => {
		const targetFrameId = grid.getAdjacentFrame(frameId, Direction.DOWN);
		navigateToFrame(targetFrameId);
	}, [frameId, grid, navigateToFrame]);

	const onArrowLeft = useCallback(() => {
		const targetFrameId = grid.getAdjacentFrame(frameId, Direction.LEFT);
		navigateToFrame(targetFrameId);
	}, [frameId, grid, navigateToFrame]);

	const onArrowRight = useCallback(() => {
		const targetFrameId = grid.getAdjacentFrame(frameId, Direction.RIGHT);
		navigateToFrame(targetFrameId);
	}, [frameId, grid, navigateToFrame]);

	// Handle tap on image to toggle controls
	const handleImageTap = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();
			toggleControls();
		},
		[toggleControls]
	);

	// Handle touch events for pinch zoom and swipe detection
	const handleTouchStart = useCallback(
		(e: TouchEvent) => {
			if (e.touches.length === 2) {
				// Pinch zoom - hide controls
				hideControls();
				touchStartRef.current = null;
			} else if (e.touches.length === 1) {
				// Single touch - record start position for swipe detection
				const touch = e.touches[0];
				touchStartRef.current = {
					x: touch.clientX,
					y: touch.clientY,
					time: Date.now(),
				};
			}
		},
		[hideControls]
	);

	const handleTouchEnd = useCallback(
		(e: TouchEvent) => {
			// Don't automatically show controls after zoom ends
			// User needs to tap to show controls again

			// Handle swipe detection
			if (touchStartRef.current && e.changedTouches.length === 1) {
				const touch = e.changedTouches[0];
				const deltaX = touch.clientX - touchStartRef.current.x;
				const deltaY = touch.clientY - touchStartRef.current.y;
				const deltaTime = Date.now() - touchStartRef.current.time;

				// Swipe thresholds
				const minSwipeDistance = 50;
				const maxSwipeTime = 300;
				const maxHorizontalSwipe = 100; // Prevent accidental horizontal swipes for vertical swipes

				// Check if it's a valid horizontal swipe
				if (
					deltaTime < maxSwipeTime &&
					Math.abs(deltaX) > minSwipeDistance &&
					Math.abs(deltaY) < maxHorizontalSwipe
				) {
					if (deltaX > 0) {
						// Swipe right - go to previous frame
						onArrowLeft();
					} else {
						// Swipe left - go to next frame
						onArrowRight();
					}
					e.stopPropagation(); // Prevent event from bubbling to tap handler
					touchStartRef.current = null;
					return; // Prevent controls from showing
				}

				// Check if it's a valid vertical swipe
				if (
					deltaTime < maxSwipeTime &&
					Math.abs(deltaY) > minSwipeDistance &&
					Math.abs(deltaX) < maxHorizontalSwipe
				) {
					if (deltaY > 0) {
						onArrowUp();
					} else {
						onArrowDown();
					}
					e.stopPropagation(); // Prevent event from bubbling to tap handler
					touchStartRef.current = null;
					return; // Prevent controls from showing
				}

				touchStartRef.current = null;
			}
		},
		[onArrowLeft, onArrowRight, onArrowUp, onArrowDown]
	);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (hideControlsTimeoutRef.current) {
				clearTimeout(hideControlsTimeoutRef.current);
			}
		};
	}, []);

	// Show controls when frame changes (but not when swiping)
	useEffect(() => {
		// Only show controls if they were visible before the frame change
		// This prevents controls from showing after swipe navigation
		if (controlsVisible) {
			showControls();
		}
	}, [frameId, showControls, controlsVisible]);

	if (!frame) {
		return null;
	}

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const openInNewTab = () => {
		if (siteUrl) window.open(siteUrl, '_blank', 'noopener,noreferrer');
	};

	const onAddToChat = () => {
		if (!activeChat || !frameId) return;

		const pendingChatInput = pendingChatInputs[activeChat.id] || {
			frameIds: [],
			message: '',
		};

		// Check if frame is already in the pending input
		if (!pendingChatInput.frameIds.includes(frameId)) {
			dispatch(
				setPendingChatInput({
					chatId: activeChat.id,
					pendingChatInput: {
						...pendingChatInput,
						frameIds: [...pendingChatInput.frameIds, frameId],
					},
				})
			);
		}
		onClose?.();
	};

	// Check if navigation is available for each direction
	const canNavigateUp = frameId ? grid.getAdjacentFrame(frameId, Direction.UP) !== null : false;
	const canNavigateDown = frameId ? grid.getAdjacentFrame(frameId, Direction.DOWN) !== null : false;
	const canNavigateLeft = frameId ? grid.getAdjacentFrame(frameId, Direction.LEFT) !== null : false;
	const canNavigateRight = frameId
		? grid.getAdjacentFrame(frameId, Direction.RIGHT) !== null
		: false;

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderLoadingOverlay = () => {
		if (frame.metadata.status === FrameStatus.PROCESSING || isImageLoading) {
			return (
				<S.LoadingOverlay>
					<Loader width={60} height={60} />
				</S.LoadingOverlay>
			);
		} else if (frame.metadata.status === FrameStatus.ERROR) {
			return (
				<S.LoadingOverlay>
					<Icon type={IconType.ERROR} width={60} height={60} color={theme.gray['400']} />
				</S.LoadingOverlay>
			);
		}
		return null;
	};

	return (
		<S.Base>
			{/* {onClose && <S.Overlay onClick={onClose} />} */}
			<S.Content>
				<S.ImageContainer
					onClick={handleImageTap}
					onTouchStart={handleTouchStart}
					onTouchEnd={handleTouchEnd}
				>
					{imageSrc && (
						<S.Image
							key={imageSrc}
							ref={handleImageRef}
							src={imageSrc}
							alt={frame.title || 'Frame'}
						/>
					)}
					{renderLoadingOverlay()}
				</S.ImageContainer>

				{/* Top Controls */}
				{controlsVisible && (
					<S.TopControls>
						{siteUrl && (
							<S.BaseButton onClick={openInNewTab}>
								<Icon
									type={IconType.OPEN_EXTERNAL}
									width={24}
									height={24}
									color={theme.gray['1000']}
								/>
							</S.BaseButton>
						)}
						{onClose && (
							<S.BaseButton onClick={onClose}>
								<Icon type={IconType.CLOSE} width={24} height={24} color={theme.gray['1000']} />
							</S.BaseButton>
						)}
					</S.TopControls>
				)}

				{/* Bottom Controls */}
				{controlsVisible && sceneControls && (
					<S.BottomControls>
						<S.ControlGroup>
							<S.BaseButton onClick={onArrowUp} disabled={!canNavigateUp}>
								<Icon
									type={IconType.LINE_ARROW_UP}
									width={24}
									height={24}
									color={theme.gray['1000']}
								/>
							</S.BaseButton>
							<S.BaseButton onClick={onArrowDown} disabled={!canNavigateDown}>
								<Icon
									type={IconType.LINE_ARROW_DOWN}
									width={24}
									height={24}
									color={theme.gray['1000']}
								/>
							</S.BaseButton>
						</S.ControlGroup>

						<S.CenterButton onClick={onAddToChat}>
							<Icon type={IconType.ADD} width={28} height={28} color={theme.gray['1000']} />
						</S.CenterButton>

						<S.ControlGroup>
							<S.BaseButton onClick={onArrowLeft} disabled={!canNavigateLeft}>
								<Icon
									type={IconType.LINE_ARROW_LEFT}
									width={24}
									height={24}
									color={theme.gray['1000']}
								/>
							</S.BaseButton>
							<S.BaseButton onClick={onArrowRight} disabled={!canNavigateRight}>
								<Icon
									type={IconType.LINE_ARROW_RIGHT}
									width={24}
									height={24}
									color={theme.gray['1000']}
								/>
							</S.BaseButton>
						</S.ControlGroup>
					</S.BottomControls>
				)}
			</S.Content>
		</S.Base>
	);
};

export default FrameGallery;
